import { observer } from 'mobx-react-lite';
import React from 'react';

import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { setAvailabilityOverride } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useCalendarStore, useCurrentuser, useTimezone } from '@uplift/contexts';
import { useFirstRef, useIsCurrentProviderLive } from '@uplift/hooks';
import { useICSSubscriptionEventsQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { CalendarStore } from '@uplift/store';
import { NEW_EVENT_ID, UserStatus, TimeRange } from '@uplift/types';
import {
  AddSessionFormFormik,
  AddSessionFormModal,
  AddSessionFormType,
  AppointmentDurationMappingDurationMinutes,
  AppointmentDurationMappingMinutesDuration, CalendarEventPopover, CalendarRef, ICSConflictsResolutionModal, ScheduleCalendar, ScrollableView,
  useEffectOnUpdateValue
} from '@uplift/ui-v2';

import { CalendarMetrics } from './calendar-metrics.component';
import { CalendarModals } from './calendar-modals';
import { ScheduleSidebar } from './sidebar';
import { formatUserFullName } from '@uplift/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';

export const useStyles = makeStyles()((theme) => ({
  sideBar: {
    background: theme.palette.grey[50],
    maxWidth: '294px',
    width: '100%',
    display: 'flex',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  calendarWrapper: {
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(3),
    paddingRight: 0,
    flex: 1,
  },
}));


export type ProviderSessionCalendarProps = {
  calendarRef: React.MutableRefObject<CalendarRef>;
  createAppointmentOpen: boolean;
  setCreateAppointmentOpen: (open: boolean) => void;
};


export const ProviderSessionsCalendar: React.FC<React.PropsWithChildren<ProviderSessionCalendarProps>> = observer(({ calendarRef, createAppointmentOpen, setCreateAppointmentOpen }) => {
  const formikContext = useFormikContext<AddSessionFormType>();
  const timezone = useTimezone();
  const newEvent = React.useMemo(() => {
    if (formikContext.values.startTime) {
      const startObj = CalendarDateService.date(formikContext.values.startTime).utc();
      const durationMinutes = AppointmentDurationMappingDurationMinutes[formikContext.values.duration] ?? AppointmentDurationMappingDurationMinutes[60];

      return {
        id: NEW_EVENT_ID,
        start: startObj.format(DateFormats.ISODayjs),
        end: startObj.add(durationMinutes, 'minutes').format(DateFormats.ISODayjs),
      };
    }

    return null;
  }, [ formikContext.values.startTime, formikContext.values.duration ]);

  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);

  const calendarStore = useCalendarStore();
  const calendarApi = calendarStore.calendarApi;
  const [ ICSResolutionOpened, setICSResolutionOpened ] = React.useState(false);
  const [ createEventAnchor, setCreateEventAnchor ] = useFirstRef<HTMLDivElement>();
  const calendarStoreRef = React.useRef<CalendarStore>(calendarStore);

  calendarStoreRef.current = calendarStore;

  const { data: icsSubscription } = useICSSubscriptionEventsQuery(me.id, {
    enabled: true,
    refetchOnMount: 'always',
  });

  const handleCloseCreateEvent = React.useCallback(() => {
    setCreateAppointmentOpen(false);
  }, []);

  const onElementCreate = React.useCallback((e: HTMLDivElement | null) => {
    if (e) {
      setCreateEventAnchor(e);
    } else {
      setCreateEventAnchor(undefined);
    }
  }, []);

  React.useEffect(() => {
    const calendarStore = calendarStoreRef.current;
    const calendarApi = calendarStore?.calendarApi;

    if (newEvent) {
      const event = calendarStoreRef.current?.calendarApi?.getEventById(NEW_EVENT_ID);

      calendarStore?.setNewEvent({
        time: {
          start: newEvent.start,
          end: newEvent.end,
        },
        timezone: timezone,
      });

      if (event) {
        event.setDates(newEvent.start, newEvent.end);
      } else {
        calendarApi.addEvent(newEvent);
      }
      setCreateAppointmentOpen(true);
    } else {
      setCreateAppointmentOpen(false);
    }
  }, [ newEvent, timezone ]);

  React.useEffect(() => {
    if (!createAppointmentOpen) {
      calendarApi?.getEventById(NEW_EVENT_ID)?.remove();
    }
  }, [ createAppointmentOpen ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffectOnUpdateValue(() => {
    if (formikContext.values.date) {
      calendarStore.calendarApi?.gotoDate(formikContext.values.date);
    }
  }, [ formikContext.values.date ]);

  const events = React.useMemo(() => {
    return calendarStore.appointments
      ?.filter(a => a.patient_id)
      .map((appointment) => {
        const patient = calendarStore.patients?.[appointment.patient_id];
        const start = CalendarDateService.date.utc(appointment.from_datetime);
        const end = CalendarDateService.date.utc(appointment.to_datetime);

        return {
          id: appointment.id,
          title: formatUserFullName({
            firstName: patient.first_name,
            lastName: patient.last_name,
            preferredFirstName: patient.preferred_name,
          }),
          start: start.format(),
          end: end.format(),
          extendedProps: {
            fromDatetime: appointment.from_datetime,
            toDatetime: appointment.to_datetime,
            parentId: appointment.appointment_series_id,
            recurring: appointment.recurring,
            state: appointment.state,
            patientId: appointment.patient_id,
            isRegular: patient?.status === UserStatus.regular,
            isMaterialized: appointment.is_materialized,
          },
        };
      });
  }, [ calendarStore.appointments, calendarStore.patients ]);

  const handleAvailabilityDateOverride = React.useCallback(async (date: DateObject, ranges: TimeRange[]) => {
    await setAvailabilityOverride({
      date: date.format(DateFormats.ISO_DATE),
      ranges: ranges,
    });

    calendarStore.loadAllEvents(calendarStore._lastInfo, true);
  }, []);

  return (
    <>
      <ScheduleCalendar
        setNewEvent={e => {
          const start = CalendarDateService.date(e.start).tz(timezone);
          const end = CalendarDateService.date(e.end).tz(timezone);
          const duration = end.diff(start, 'minutes') as keyof typeof AppointmentDurationMappingMinutesDuration;
          const newValues = {
            ...formikContext.values,
            date: start.format(DateFormats.ISO_DATE),
            startTime: start.format(),
            duration: AppointmentDurationMappingMinutesDuration[duration],
          };

          formikContext.setValues(newValues);
        }}
        calendarRef={calendarRef}
        availabilityEditing={true}
        events={events}
        loadAllEvents={calendarStore.loadAllEvents}
        isCalendarLoading={calendarStore.isCalendarLoading}
        checkSlotAvailability={calendarStore.isRangeAvailableForBooking}
        availableSlots={calendarStore.allowedSlots}
        openModal={calendarStore.openModal}
        setSelectedEvent={calendarStore.setSelectedEvent}
        calendarApi={calendarApi}
        setCalendarApi={calendarStore.setCalendarApi}
        newEventAnchorRef={onElementCreate}
        availabilityTemplate={calendarStore.availabilityTemplate}
        overrides={calendarStore.availabilityOverrides}
        onAvailabilityDateOverride={handleAvailabilityDateOverride}
        icsEvents={icsSubscription?.events ?? []}
        icsConflicts={icsSubscription?.conflicts ?? []}
        onICSConflictsOpen={() => setICSResolutionOpened(true)}
        allowToCreateAppointments={isProviderInLiveStatus}
      />
      <CalendarEventPopover
        open={createAppointmentOpen}
        onClose={handleCloseCreateEvent}
        anchor={createEventAnchor}
        onExited={React.useCallback(() => formikContext.resetForm(), [])}
      >
        <Box width="500px">
          <AddSessionFormModal open={createAppointmentOpen} onClose={handleCloseCreateEvent} />
        </Box>
      </CalendarEventPopover>
      <ICSConflictsResolutionModal
        open={ICSResolutionOpened}
        onClose={() => setICSResolutionOpened(false)}
        keepMounted
      />
    </>
  );
});


export const CalendarDesktop: React.FC = () => {
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);
  const { classes } = useStyles();
  const calendarRef = React.useRef<CalendarRef>();
  const calendarStore = useCalendarStore();
  const [ createAppointmentOpen, setCreateAppointmentOpen ] = React.useState(false);

  return (
    <AddSessionFormFormik
      onSuccess={async () => {
        await queryClient.invalidateQueries([ 'icsSubscription' ]);
        setCreateAppointmentOpen(false);
        await calendarStore.loadAllEvents(calendarStore._lastInfo, true, false);
      }}
    >
      <>
        <ScrollableView className={classes.sideBar}>
          <Box p={3}>
            <ScheduleSidebar calendarRef={calendarRef} />
          </Box>
        </ScrollableView>
        <Box className={classes.content}>
          <Box className={classes.calendarWrapper}>
            <ProviderSessionsCalendar
              calendarRef={calendarRef}
              createAppointmentOpen={createAppointmentOpen}
              setCreateAppointmentOpen={setCreateAppointmentOpen}
            />
          </Box>
          {isProviderInLiveStatus && <CalendarMetrics />}
        </Box>
        <CalendarModals />
      </>
    </AddSessionFormFormik>
  );
};
